import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { DateTime } from "luxon";
import { createBackgroundJob } from "@/api/background-job/createBackgroundJob";
import { getBackgroundJobListApi } from "@/api/background-job/getBackgroundJobList";
import type { ReportUploadKind } from "@/api/report/_models/ReportUpload";
import { useAppDispatch } from "@/app/hooks";
import {
  replaceSnapshotDayInUrl,
  SNAPSHOT_BUILD_JOB_KIND,
  storeSnapshotDay,
  toErrorText,
  toInitialSnapshotDay,
  toSnapshotBuildSubject,
  toYyMmDd,
} from "./snapshot.utils";
import type { SnapshotSectionSummary } from "./snapshot.utils";
import { useSnapshotUpload } from "./useSnapshotUpload";
import type { SnapshotUploadState } from "./useSnapshotUpload";

/** A report's build start: the request in flight, or refused (e.g. already building). */
export interface SnapshotBuildStart {
  isStarting: boolean;
  error: string | null;
}

export type SnapshotBuildStartState = Record<ReportUploadKind, SnapshotBuildStart>;

export type SnapshotToastSeverity = "success" | "warning" | "error";

/** A short message in the page's corner (SnapshotToasts). */
export interface SnapshotToast {
  id: number;
  message: string;
  severity: SnapshotToastSeverity;
}

/** Each section on the page, as it reports itself (setSectionSummary). */
export type SnapshotSectionSummaries = Partial<Record<ReportUploadKind, SnapshotSectionSummary>>;

/** A Snapshot page's state: the day picked, each report's uploads and build start, and the page-wide actions. */
export interface SnapshotState {
  day: DateTime;
  /** The day as yyMMdd. */
  date: string;
  dateLabel: string;
  setDay: (day: DateTime) => void;
  /** Files dropped on this page, per report. */
  uploads: SnapshotUploadState;
  isUploading: boolean;
  buildStarts: SnapshotBuildStartState;
  sectionSummaries: SnapshotSectionSummaries;
  /** The sections that need building (what "Build all" builds). */
  pendingReports: ReportUploadKind[];
  isAnyBuilding: boolean;
  addFiles: (report: ReportUploadKind, files: File[]) => void;
  retryFile: (report: ReportUploadKind, id: string) => void;
  removeFile: (report: ReportUploadKind, id: string) => void;
  /** A file was deleted from S3 (by its key). */
  markFileDeleted: (report: ReportUploadKind, key: string) => void;
  /** Starts these reports' builds for the day. */
  build: (reports: ReportUploadKind[]) => void;
  buildAll: () => void;
  /** A section reports itself; null when it leaves the page. */
  setSectionSummary: (report: ReportUploadKind, summary: SnapshotSectionSummary | null) => void;
  toasts: SnapshotToast[];
  notify: (message: string, severity?: SnapshotToastSeverity) => void;
  dismissToast: (id: number) => void;
}

/** The Snapshot page's state, provided by SnapshotProvider; read with useSnapshot. */
export const SnapshotContext = createContext<SnapshotState | null>(null);

const IDLE_START: SnapshotBuildStart = { isStarting: false, error: null };

const IDLE_STARTS: SnapshotBuildStartState = {
  toe: IDLE_START,
  toll: IDLE_START,
  estore: IDLE_START,
  "toll-inventory": IDLE_START,
  "estore-inventory": IDLE_START,
};

interface SnapshotProviderProps {
  children: ReactNode;
}

const TOAST_MS = 3500;

/**
 * Owns a Snapshot page's state. The day starts from the URL, else the last one picked, else today.
 * A new day drops the uploads and refused starts; a build already running carries on, on the server.
 */
export const SnapshotProvider = (props: SnapshotProviderProps) => {
  const { children } = props;
  const [day, setDayState] = useState<DateTime>(() => toInitialSnapshotDay(window.location.search));
  const [buildStarts, setBuildStarts] = useState<SnapshotBuildStartState>(IDLE_STARTS);
  const [sectionSummaries, setSectionSummaries] = useState<SnapshotSectionSummaries>({});
  const [toasts, setToasts] = useState<SnapshotToast[]>([]);
  const nextToastId = useRef(1);
  const upload = useSnapshotUpload();
  const [startJob] = createBackgroundJob.useMutation();
  const dispatch = useAppDispatch();
  const date = toYyMmDd(day);
  const reports = Object.keys(sectionSummaries) as ReportUploadKind[];
  const pendingReports = reports.filter((report) => sectionSummaries[report]?.needsBuild === true);
  const isAnyBuilding = reports.some((report) => sectionSummaries[report]?.status === "building");

  const updateBuildStart = (report: ReportUploadKind, start: SnapshotBuildStart) => {
    setBuildStarts((current) => {
      return { ...current, [report]: start };
    });
  };

  const startBuild = async (report: ReportUploadKind) => {
    updateBuildStart(report, { isStarting: true, error: null });
    try {
      const job = await startJob({ kind: SNAPSHOT_BUILD_JOB_KIND, input: { report, date } }).unwrap();
      const jobListArgs = { kind: SNAPSHOT_BUILD_JOB_KIND, subject: toSnapshotBuildSubject(report, date), limit: 1 };
      const jobList = [job];
      dispatch(getBackgroundJobListApi.util.upsertQueryData("getBackgroundJobList", jobListArgs, jobList));
      updateBuildStart(report, IDLE_START);
    }
    catch (e) {
      updateBuildStart(report, { isStarting: false, error: toErrorText(e) });
    }
  };

  const build = (reports: ReportUploadKind[]) => {
    void Promise.allSettled(reports.map((report) => startBuild(report)));
  };

  useEffect(() => {
    replaceSnapshotDayInUrl(day);
    storeSnapshotDay(day);
  }, [day]);

  const setDay = (next: DateTime) => {
    if (!next.isValid) return;
    setDayState(next);
    upload.resetForDate();
    setBuildStarts(IDLE_STARTS);
  };

  const setSectionSummary = useCallback((report: ReportUploadKind, summary: SnapshotSectionSummary | null) => {
    setSectionSummaries((current) => {
      if (!summary) {
        if (!(report in current)) return current;
        const next = { ...current };
        delete next[report];
        return next;
      }
      if (JSON.stringify(current[report]) === JSON.stringify(summary)) return current;
      return { ...current, [report]: summary };
    });
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback((message: string, severity: SnapshotToastSeverity = "success") => {
    const id = nextToastId.current;
    nextToastId.current += 1;
    setToasts((current) => [...current, { id, message, severity }]);
    window.setTimeout(() => dismissToast(id), TOAST_MS);
  }, [dismissToast]);

  const snapshot: SnapshotState = {
    day,
    date,
    dateLabel: day.toFormat("d LLL yyyy"),
    setDay,
    uploads: upload.state,
    isUploading: upload.isUploading,
    buildStarts,
    sectionSummaries,
    pendingReports,
    isAnyBuilding,
    addFiles: (report, files) => upload.addFiles(report, files, date),
    retryFile: (report, id) => upload.retryFile(report, id, date),
    removeFile: (report, id) => upload.removeFile(report, id),
    markFileDeleted: (report, key) => upload.markFileDeleted(report, key),
    build,
    buildAll: () => build(pendingReports),
    setSectionSummary,
    toasts,
    notify,
    dismissToast,
  };

  return (
    <SnapshotContext.Provider value={snapshot}>
      {children}
    </SnapshotContext.Provider>
  );
};

/** The Snapshot page's state around this component; throws outside a SnapshotProvider. */
export const useSnapshot = (): SnapshotState => {
  const snapshot = useContext(SnapshotContext);
  if (!snapshot) throw new Error("useSnapshot must be used inside a Snapshot page");
  return snapshot;
};
